import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  AlertTriangle, Briefcase, MessageSquare, Camera, CheckCircle2, CheckSquare, ChevronRight, Mic, Image as ImageIcon, FileText, Ruler, Package, Languages, Trash2, CalendarDays, UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { AddressLink } from "../components/AddressLink";
import { WeatherChip } from "../components/WeatherChip";
import { DailySummaryCard } from "../components/DailySummaryCard";
import { Sparkles } from "lucide-react";


export default function Home() {
  const { user, t, lang } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [summaries, setSummaries] = useState([]); // today's AI daily summaries (supervisors)

  const load = useCallback(async () => {
    const [dash, jobsRes] = await Promise.all([
      api.get(`/dashboard?user_id=${user.id}`),
      api.get(`/jobs?user_id=${user.id}`),
    ]);
    setData(dash.data);
    setJobs(jobsRes.data);
    // Daily summary cards (supervisor home) — best effort
    if (user.role !== "installer") {
      const d = new Date();
      const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      api.get(`/daily-summaries?date=${ds}&user_id=${user.id}`)
        .then((r) => setSummaries(Array.isArray(r.data) ? r.data : []))
        .catch(() => {});
    }
  }, [user.id, user.role]);

  useEffect(() => { load(); }, [load]);

  // Recent feed follows the viewer's language: translate items sent in another language
  if (!data) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-24 bg-muted rounded-xl animate-pulse" />
        <div className="h-48 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const s = data.stats;
  const isInstaller = user.role === "installer";
  // only TODAY's jobs (multi-day in-progress included) — never the ones removed from the PMHR matrix
  const activeJobs = jobs.filter((j) => j.time_bucket === "active" && !j.removed_from_source);

  const stats = isInstaller
    ? [
        { key: "active_jobs", label: t("my_jobs"), value: activeJobs.length, icon: Briefcase, color: "text-primary" },
        { key: "open_tasks", label: t("open_tasks"), value: s.open_tasks, icon: CheckSquare, color: "text-accent-foreground" },
        { key: "open_issues", label: t("open_issues"), value: s.open_issues, icon: AlertTriangle, color: "text-destructive" },
        { key: "unread_conversations", label: t("unread_conversations"), value: s.unread_conversations, icon: MessageSquare, color: "text-primary" },
      ]
    : [
        { key: "open_issues", label: t("open_issues"), value: s.open_issues, icon: AlertTriangle, color: "text-destructive" },
        { key: "open_tasks", label: t("open_tasks"), value: s.open_tasks, icon: CheckSquare, color: "text-accent-foreground" },
        { key: "active_jobs", label: t("jobs"), value: s.active_jobs, icon: Briefcase, color: "text-primary" },
        { key: "unread_conversations", label: t("unread_chat"), value: s.unread_conversations, icon: MessageSquare, color: "text-primary" },
      ];

  return (
    <div className="flex-1">
      <div className="header-wash border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("today")}</p>
            <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mt-1 truncate">
              {lang === "pt" ? `Olá, ${user.name.split(" ")[0]}` : `Hi, ${user.name.split(" ")[0]}`}
            </h1>
          </div>
          <div className="flex flex-col items-center shrink-0 md:hidden" data-testid="home-brand-logo">
            <img src="/onsitecomms-icon.png" alt="OnSiteComms" className="h-12 w-12 rounded-xl object-contain" />
            <span className="font-display font-semibold text-[11px] leading-none mt-1">
              <span className="text-[#2B3990]">OnSite</span><span className="text-[#C9A03B]">Comms</span>
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8">
        {/* Stats: 2x2 grid on phones, single row on desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
          {stats.map((st) => (
            <Card
              key={st.key}
              data-testid={`today-stats-${st.key.replace(/_/g, "-")}`}
              className="p-3 sm:p-4 cursor-pointer hover:bg-muted/40 transition-colors"
              onClick={() => {
                if (st.key === "open_issues") navigate("/issues");
                else if (st.key === "active_jobs") navigate("/jobs");
                else if (st.key === "unread_conversations") navigate("/messages");
                else if (st.key === "open_tasks") navigate("/tasks");
                else navigate("/jobs");
              }}
            >
              <st.icon className={`h-5 w-5 ${st.color}`} strokeWidth={2} />
              <div className="text-2xl sm:text-3xl font-display font-semibold tabular-nums mt-2">{st.value}</div>
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide mt-0.5">{st.label}</div>
            </Card>
          ))}
        </div>

        {/* AI Daily Summary — supervisors (today's cards, compact) */}
        {!isInstaller && (
          <div className="space-y-3" data-testid="home-daily-summary-section">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-lg flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-accent-foreground" /> {t("daily_summary")}
              </h2>
              <Button variant="ghost" size="sm" data-testid="view-all-summaries" onClick={() => navigate("/summary")}>
                {t("view_all")} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            {summaries.length === 0 ? (
              <Card
                className="p-4 text-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/30 transition-colors"
                data-testid="home-summary-empty"
                onClick={() => navigate("/summary")}
              >
                {t("summary_empty_home")}
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {summaries.slice(0, 4).map((s) => <DailySummaryCard key={s.id} summary={s} compact />)}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Jobs list */}
          <div className="lg:col-span-7 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-semibold text-lg">{isInstaller ? t("my_jobs") : t("active_jobs")}</h2>
              <Button variant="ghost" size="sm" data-testid="view-all-jobs" onClick={() => navigate("/jobs")}>
                {t("view_all")} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
            {activeJobs.length === 0 && (
              <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="home-no-jobs-today">
                {t("no_jobs_today")}
              </Card>
            )}
            {activeJobs.slice(0, 4).map((job) => (
              <Card
                key={job.id}
                data-testid={`home-job-card-${job.job_number}`}
                className="p-4 cursor-pointer hover:bg-muted/40 transition-colors"
                onClick={() => navigate(`/jobs/${job.id}`)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      {job.crew?.code && (
                        <Badge data-testid="home-job-crew-badge" className="bg-primary text-primary-foreground font-mono-plex text-[10px] tracking-wider px-1.5 py-0">
                          {job.crew.code}
                        </Badge>
                      )}
                      {job.supervisor?.name && (
                        <Badge data-testid="home-job-supervisor-badge" variant="outline" className="text-[10px] px-1.5 py-0 flex items-center gap-1 border-primary/40 text-primary font-medium">
                          <UserRound className="h-2.5 w-2.5" /> {job.supervisor.name}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono-plex text-xs font-semibold text-primary">#{job.job_number}</span>
                      <span className="font-medium">{job.customer_name}</span>
                      <Badge variant={job.status === "active" ? "default" : "secondary"} className="text-[10px]">
                        {t(job.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1 min-w-0">
                      <AddressLink address={job.address} city={job.city} iconClass="h-3.5 w-3.5">{job.address}</AddressLink>
                      {job.distance_label && <span className="text-xs">· {job.distance_label}</span>}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {job.description} · {t("crew")} {job.crew?.code} · {job.supervisor?.name}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <WeatherChip city={job.city} size="sm" />
                    {job.scheduled_date && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1 tabular-nums" data-testid="home-job-date">
                        <CalendarDays className="h-3 w-3" />
                        {new Date(job.scheduled_date).toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", { day: "numeric", month: "short" })}
                      </span>
                    )}
                    {job.open_issues > 0 && (
                      <Badge className="bg-destructive text-destructive-foreground text-[10px]">
                        {job.open_issues} {t("issues").toLowerCase()}
                      </Badge>
                    )}
                    <span className="text-xs text-muted-foreground">{job.photo_count} {t("photos").toLowerCase()}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>

        </div>
      </div>
    </div>
  );
}
